import { useState, useEffect, useCallback, useRef } from "react";
import { X, Building2, ExternalLink, DollarSign, UserMinus } from "lucide-react";
import type { UpworkJob, JobAssignee, BulkAssignResult } from "../api/UpworkJobsApi";
import { fetchJobAssignees, STATUS_OPTIONS, TERMINAL } from "../api/UpworkJobsApi";
import HighlightedText from "shared/HighlightedText";
import MultiAssignDropdown from "shared/MultiAssignDropdown";

const STATUS_CHIP: Record<string, { label: string; className: string }> = Object.fromEntries(
  STATUS_OPTIONS.map((o) => [o.value, { label: o.label, className: TERMINAL.has(o.value) ? "bg-muted text-muted-foreground border-border" : "bg-primary text-primary-foreground border-primary/70" }])
);

function clientName(clientInfoJson: string | null | undefined): string {
  if (!clientInfoJson) return "Client";
  try {
    const parsed = JSON.parse(clientInfoJson);
    return parsed.companyName || "Client";
  } catch {
    return "Client";
  }
}

interface Props {
  job: UpworkJob;
  techManagers: { id: number; name: string }[];
  onClose: () => void;
  onAssign: (jobId: number, employeeIds: number[]) => Promise<BulkAssignResult>;
  onUnassign: (jobId: number, employeeId: number) => Promise<void>;
}

export default function JobDetailModal({ job, techManagers, onClose, onAssign, onUnassign }: Props) {
  const [assignError, setAssignError] = useState<string | null>(null);
  const [assignSuccess, setAssignSuccess] = useState<string | null>(null);

  const [assignees, setAssignees] = useState<JobAssignee[]>([]);
  const [assigneesLoading, setAssigneesLoading] = useState(true);
  const [assigneesError, setAssigneesError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [unassignError, setUnassignError] = useState<string | null>(null);

  const requestId = useRef(0);

  const loadAssignees = useCallback(async () => {
    const id = ++requestId.current;
    setAssigneesLoading(true);
    setAssigneesError(null);
    try {
      const result = await fetchJobAssignees(job.id);
      if (id !== requestId.current) return;
      setAssignees(result);
    } catch {
      if (id !== requestId.current) return;
      setAssignees([]);
      setAssigneesError("Failed to load assignees. Please try again.");
    } finally {
      if (id === requestId.current) setAssigneesLoading(false);
    }
  }, [job.id]);

  useEffect(() => { loadAssignees(); }, [loadAssignees]);

  async function doAssign(employeeIds: number[]) {
    setAssignError(null);
    setAssignSuccess(null);
    try {
      const result = await onAssign(job.id, employeeIds);
      if (result.errors.length > 0) {
        const detail = result.errors
          .map((e) => `${techManagers.find((t) => t.id === e.employee_id)?.name ?? `#${e.employee_id}`}: ${e.detail}`)
          .join("; ");
        setAssignError(`Assigned ${result.assigned.length}, failed for ${result.errors.length} — ${detail}`);
      } else {
        setAssignSuccess(`Assigned ${employeeIds.length} ${employeeIds.length === 1 ? "TM" : "TMs"} successfully.`);
      }
    } catch (err: any) {
      // Don't also set assignError here — MultiAssignDropdown surfaces this same
      // message itself (via the rethrow below) so the two don't end up shown at once.
      throw new Error(err?.response?.data?.detail ?? "Failed to assign. Please try again.");
    } finally {
      await loadAssignees();
    }
  }

  async function doUnassign(employeeId: number) {
    setRemovingId(employeeId);
    setUnassignError(null);
    try {
      await onUnassign(job.id, employeeId);
    } catch (err: any) {
      setUnassignError(err?.response?.data?.detail ?? "Failed to remove assignment. Please try again.");
    } finally {
      await loadAssignees();
      setRemovingId(null);
    }
  }

  const description = job.description?.trim();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="relative w-full max-w-2xl max-h-[90vh] bg-surface border border-border rounded-2xl flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start gap-4 p-6 border-b border-border shrink-0">
          <div className="w-12 h-12 rounded-xl border border-border bg-surface-muted flex items-center justify-center shrink-0">
            <span className="text-lg font-bold text-primary font-inter">{job.title.charAt(0).toUpperCase()}</span>
          </div>

          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-foreground font-inter leading-snug">{job.title}</h2>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
              <span className="flex items-center gap-1 text-xs text-muted-foreground font-inter">
                <Building2 className="w-3 h-3" />
                {clientName(job.client_info)}
              </span>
              {job.amount_display_value && (
                <span className="flex items-center gap-1 text-xs text-emerald-400 font-inter">
                  <DollarSign className="w-3 h-3" />
                  {job.amount_display_value}
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              <span className="text-[11px] px-2 py-1 rounded-lg border bg-primary/10 text-primary border-primary/20 font-inter">
                {job.search_profile}
              </span>
              {job.experience_level && (
                <span className="text-[11px] px-2 py-1 rounded-lg border bg-muted/50 text-muted-foreground border-border font-inter">
                  {job.experience_level}
                </span>
              )}
              {!job.is_active && (
                <span className="text-[11px] px-2 py-1 rounded-lg border bg-red-500/10 text-red-400 border-red-500/20 font-inter">
                  Inactive
                </span>
              )}
            </div>
          </div>

          <button onClick={onClose} className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-surface-muted rounded-lg transition-colors shrink-0 self-start">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Assign to TM */}
        <div className="px-6 py-4 border-b border-border shrink-0">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground font-inter mb-2">Assigned To</p>
          {assigneesLoading ? (
            <p className="text-xs text-muted-foreground font-inter mb-3">Loading…</p>
          ) : assigneesError ? (
            <p className="text-xs text-destructive font-inter mb-3">
              {assigneesError} <button onClick={loadAssignees} className="underline hover:text-destructive/80">Retry</button>
            </p>
          ) : assignees.length === 0 ? (
            <p className="text-xs text-muted-foreground font-inter italic mb-3">Not assigned to anyone yet.</p>
          ) : (
            <div className="flex flex-wrap items-center gap-1.5 mb-3">
              {assignees.map((a) => {
                const statusEntry = STATUS_CHIP[a.status];
                return (
                  <span key={a.employee_id} className="flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-lg border bg-surface-muted border-border text-xs text-foreground font-inter">
                    {a.name}
                    {statusEntry && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border font-inter font-medium ${statusEntry.className}`}>
                        {statusEntry.label}
                      </span>
                    )}
                    <button
                      onClick={() => doUnassign(a.employee_id)}
                      disabled={removingId === a.employee_id}
                      title="Remove assignment"
                      className="p-0.5 text-muted-foreground hover:text-destructive disabled:opacity-40 transition-colors"
                    >
                      <UserMinus className="w-3 h-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
          {unassignError && <p className="text-[11px] text-destructive font-inter mb-3">{unassignError}</p>}

          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground font-inter mb-2">Assign to Technical Manager</p>
          <MultiAssignDropdown
            options={techManagers
              .filter((tm) => !assignees.some((a) => a.employee_id === tm.id))
              .map((tm) => ({ id: tm.id, label: tm.name }))}
            onConfirm={doAssign}
            placeholder="Select TMs…"
            confirmLabel={(n) => (n > 0 ? `Assign ${n}` : "Assign")}
            emptyMessage="All eligible TMs are already assigned."
          />
          {assignError && <p className="text-[11px] text-destructive font-inter mt-1">{assignError}</p>}
          {assignSuccess && <p className="text-[11px] text-emerald-400 font-inter mt-1">{assignSuccess}</p>}
        </div>

        {/* Description */}
        <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
          {description ? (
            <div className="text-sm text-foreground font-inter leading-relaxed whitespace-pre-wrap">
              <HighlightedText text={description} />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground font-inter italic">
              No description available — view it on Upwork.
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border shrink-0">
          <a
            href={job.job_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-inter font-medium rounded-xl transition-colors whitespace-nowrap"
          >
            <ExternalLink className="w-4 h-4" />
            View on Upwork
          </a>
        </div>
      </div>
    </div>
  );
}
